import { useEffect, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { hasPermission } from "../lib/permissions";
import { inr, fmtDate } from "../lib/format";
import { FileSpreadsheet, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { DataPanel, EmptyState, MetricStrip, TableSurface } from "../components/CommandSystem";

const REPORT_TYPES = [
  { id: "overview", label: "Complete Workbook", description: "Clients, campaigns, inventory, links and summaries" },
  { id: "occupancy", label: "Occupancy Report", description: "Status & occupancy across hoardings" },
  { id: "monthly", label: "Monthly Performance", description: "Revenue & bookings per month" },
  { id: "expiring", label: "Expiring Campaigns", description: "Campaigns ending in 30/60/90 days" },
  { id: "available", label: "Available Hoardings", description: "All currently bookable inventory" },
  { id: "city", label: "City Performance", description: "Per-city revenue and occupancy" },
];

const EMPTY_DATA = {
  hoardings: [],
  clients: [],
  campaigns: [],
};

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function n(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function campaignCharge(value) {
  if (value && typeof value === "object") {
    return {
      display: n(value.display),
      printing: n(value.printing),
      installation: n(value.installation),
    };
  }
  return { display: n(value), printing: 0, installation: 0 };
}

function campaignChargeTotal(value) {
  const charge = campaignCharge(value);
  return charge.display + charge.printing + charge.installation;
}

function dateValue(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function compactId(value) {
  return String(value || "").slice(0, 8);
}

function html(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function getClientId(client) {
  return client.id || client.client_id || "";
}

function getHoardingPk(hoarding) {
  return hoarding.id || hoarding.hoarding_pk || hoarding.hoarding_id || "";
}

function getHoardingCode(hoarding) {
  return hoarding.hoarding_id || hoarding.code || hoarding.id || "";
}

function getCampaignId(campaign) {
  return campaign.id || campaign.campaign_pk || campaign.campaign_id || "";
}

function buildModel(data) {
  const hoardings = asArray(data.hoardings);
  const clients = asArray(data.clients);
  const campaigns = asArray(data.campaigns);
  const today = Date.now();

  const clientById = new Map(clients.map((c) => [getClientId(c), c]));
  const hoardingByAnyId = new Map();
  hoardings.forEach((h) => {
    hoardingByAnyId.set(getHoardingPk(h), h);
    hoardingByAnyId.set(getHoardingCode(h), h);
  });

  const links = [];
  campaigns.forEach((campaign) => {
    const assigned = asArray(campaign.assigned_hoardings);
    const ids = assigned.length ? assigned.map((h) => h.id || h.hoarding_id) : asArray(campaign.hoarding_ids);
    ids.forEach((hid) => {
      const h = hoardingByAnyId.get(hid) || assigned.find((item) => (item.id || item.hoarding_id) === hid) || {};
      links.push({
        campaign_id: getCampaignId(campaign),
        campaign_name: campaign.name || campaign.campaign_id || "",
        client_id: campaign.client_id || "",
        client_name: campaign.client_name || clientById.get(campaign.client_id)?.company || "",
        hoarding_id: getHoardingCode(h) || hid,
        hoarding_pk: getHoardingPk(h) || hid,
        hoarding_name: h.name || "",
        city: campaign.city || h.city || "",
        status: campaign.status || "",
        start_date: dateValue(campaign.start_date),
        end_date: dateValue(campaign.end_date),
        contract_amount: campaignChargeTotal(campaign.contract_amounts?.[hid] || campaign.contract_amounts?.[getHoardingPk(h)] || h.monthly_price || 0),
      });
    });
  });

  const cityMap = {};
  hoardings.forEach((h) => {
    const city = h.city || "Unassigned";
    const g = cityMap[city] || { city, total: 0, occupied: 0, reserved: 0, available: 0, maintenance: 0, revenue: 0, price_capacity: 0 };
    g.total += 1;
    g.price_capacity += n(h.monthly_price);
    if (h.occupancy_status === "Occupied") {
      g.occupied += 1;
      g.revenue += n(h.monthly_price);
    } else if (h.occupancy_status === "Reserved") {
      g.reserved += 1;
    } else if (h.occupancy_status === "Maintenance") {
      g.maintenance += 1;
    } else {
      g.available += 1;
    }
    cityMap[city] = g;
  });

  const cities = Object.values(cityMap)
    .map((g) => ({ ...g, occupancy_pct: g.total ? Math.round((g.occupied / g.total) * 1000) / 10 : 0 }))
    .sort((a, b) => b.revenue - a.revenue);

  const monthMap = {};
  campaigns.forEach((c) => {
    const start = dateValue(c.start_date);
    if (!start) return;
    const month = start.slice(0, 7);
    const g = monthMap[month] || { month, campaigns: 0, hoardings: 0, revenue: 0 };
    g.campaigns += 1;
    g.hoardings += asArray(c.hoarding_ids).length || asArray(c.assigned_hoardings).length || n(c.hoarding_count);
    g.revenue += n(c.revenue || Object.values(c.contract_amounts || {}).reduce((sum, amount) => sum + campaignChargeTotal(amount), 0));
    monthMap[month] = g;
  });
  const monthly = Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));

  const expiring = hoardings
    .filter((h) => {
      if (!h.campaign_end) return false;
      const t = new Date(h.campaign_end).getTime();
      return t >= today && t - today <= 90 * 86400000;
    })
    .sort((a, b) => new Date(a.campaign_end) - new Date(b.campaign_end));

  const available = hoardings.filter((h) => h.occupancy_status === "Available");
  const occupied = hoardings.filter((h) => h.occupancy_status === "Occupied");
  const revenue = occupied.reduce((sum, h) => sum + n(h.monthly_price), 0);
  const capacity = hoardings.reduce((sum, h) => sum + n(h.monthly_price), 0);

  return {
    ...data,
    links,
    cities,
    monthly,
    expiring,
    available,
    summary: {
      total_hoardings: hoardings.length,
      occupied_hoardings: occupied.length,
      available_hoardings: available.length,
      clients: clients.length,
      campaigns: campaigns.length,
      monthly_revenue: revenue,
      monthly_capacity: capacity,
      occupancy_pct: hoardings.length ? Math.round((occupied.length / hoardings.length) * 1000) / 10 : 0,
      revenue_efficiency_pct: capacity ? Math.round((revenue / capacity) * 1000) / 10 : 0,
      top_city: cities[0]?.city || "",
    },
  };
}

export function Reports() {
  const { user } = useAuth();
  const [reportData, setReportData] = useState(EMPTY_DATA);
  const [type, setType] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [exportingExcel, setExportingExcel] = useState(false);
  const canExport = hasPermission(user, "export_reports");

  async function load() {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/data", { skipGlobalLoader: true });
      setReportData({
        hoardings: asArray(d.hoardings),
        clients: asArray(d.clients),
        campaigns: asArray(d.campaigns),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load report data");
      setReportData(EMPTY_DATA);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const model = useMemo(() => buildModel(reportData), [reportData]);

  function rowsForType() {
    if (type === "overview") {
      return [
        { metric: "Clients", value: model.summary.clients },
        { metric: "Hoardings", value: model.summary.total_hoardings },
        { metric: "Campaigns", value: model.summary.campaigns },
        { metric: "Portfolio occupancy", value: `${model.summary.occupancy_pct}%` },
        { metric: "Monthly revenue", value: inr(model.summary.monthly_revenue) },
        { metric: "Top city", value: model.summary.top_city || "-" },
      ];
    }
    if (type === "occupancy") {
      return model.hoardings.map((h) => ({
        id: getHoardingCode(h), name: h.name, city: h.city, area: h.area,
        status: h.occupancy_status, client: h.client_name || "-",
        price: inr(h.monthly_price), end: fmtDate(h.campaign_end),
      }));
    }
    if (type === "expiring") {
      return model.expiring.map((h) => ({
        id: getHoardingCode(h), name: h.name, city: h.city,
        client: h.client_name || "-", end: fmtDate(h.campaign_end), price: inr(h.monthly_price),
      }));
    }
    if (type === "available") {
      return model.available.map((h) => ({
        id: getHoardingCode(h), name: h.name, city: h.city, area: h.area, type: h.type, price: inr(h.monthly_price),
      }));
    }
    if (type === "city") {
      return model.cities.map((g) => ({
        city: g.city, total: g.total, occupied: g.occupied,
        occupancy: `${g.occupancy_pct}%`, revenue: inr(g.revenue),
      }));
    }
    if (type === "monthly") {
      return model.monthly.map((g) => ({
        month: g.month, campaigns: g.campaigns, hoardings: g.hoardings, revenue: inr(g.revenue),
      }));
    }
    return [];
  }

  const rows = rowsForType();
  const cols = rows[0] ? Object.keys(rows[0]) : [];

  async function exportPowerExcel() {
    setExportingExcel(true);
    try {
      const { data } = await api.get("/reports/power-excel", { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `hoardexa-power-excel-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel workbook downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Excel export failed");
    } finally {
      setExportingExcel(false);
    }
  }

  function sectionTable(title, rowsToRender, columns) {
    return `
      <section>
        <h2>${html(title)}</h2>
        <table>
          <thead><tr>${columns.map((c) => `<th>${html(c.label)}</th>`).join("")}</tr></thead>
          <tbody>${rowsToRender.map((row) => `<tr>${columns.map((c) => `<td>${html(typeof c.value === "function" ? c.value(row) : row[c.key])}</td>`).join("")}</tr>`).join("")}</tbody>
        </table>
      </section>`;
  }

  function exportPDF() {
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Please allow popups to generate the PDF");
      return;
    }
    const cityRows = model.cities.slice(0, 12);
    const expiringRows = model.expiring.slice(0, 25);
    const topClients = model.clients
      .slice()
      .sort((a, b) => n(b.monthly_spend) - n(a.monthly_spend))
      .slice(0, 20);
    const availableRows = model.available.slice(0, 25);
    const campaignRows = model.campaigns.slice(0, 25);
    win.document.write(`<!doctype html>
      <html><head><title>Hoardexa Portfolio Report</title>
      <style>
        @page{size:A4;margin:16mm}
        *{box-sizing:border-box}
        body{font-family:Inter,Manrope,Arial,sans-serif;color:#172033;margin:0;background:#fff}
        .brand{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #e11d48;padding:20px 0 16px;margin-bottom:22px}
        .logo{font-size:28px;font-weight:900;letter-spacing:.2px;color:#111827}
        .logo span{color:#e11d48}
        .meta{text-align:right;color:#64748b;font-size:12px;line-height:1.5}
        h1{font-size:26px;margin:0 0 8px;color:#111827}
        h2{font-size:15px;text-transform:uppercase;letter-spacing:.08em;color:#334155;margin:28px 0 10px}
        .subtitle{color:#64748b;margin-bottom:18px}
        .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:18px 0 20px}
        .kpi{border:1px solid #e2e8f0;border-left:4px solid #e11d48;padding:12px;background:#fff}
        .kpi b{display:block;font-size:18px;color:#111827;margin-top:4px}
        .kpi span{font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.06em}
        table{width:100%;border-collapse:collapse;font-size:10px;page-break-inside:auto}
        th{background:#111827;color:white;text-align:left;padding:7px;border:1px solid #111827}
        td{padding:6px;border:1px solid #e2e8f0;vertical-align:top}
        tr:nth-child(even) td{background:#f8fafc}
        section{page-break-inside:auto;margin-top:14px}
        .note{font-size:11px;color:#475569;background:#fff1f2;border:1px solid #fecdd3;padding:10px;margin-top:14px}
        .footer{margin-top:28px;border-top:1px solid #e2e8f0;padding-top:10px;color:#64748b;font-size:10px}
      </style></head><body>
        <div class="brand">
          <div><div class="logo">Hoard<span>exa</span></div><div class="subtitle">Complete portfolio operating report</div></div>
          <div class="meta">Generated ${html(new Date().toLocaleString())}<br/>Excel workbook contains full raw data and relationship tables</div>
        </div>
        <h1>Portfolio Report</h1>
        <div class="kpis">
          <div class="kpi"><span>Clients</span><b>${model.summary.clients}</b></div>
          <div class="kpi"><span>Hoardings</span><b>${model.summary.total_hoardings}</b></div>
          <div class="kpi"><span>Occupancy</span><b>${model.summary.occupancy_pct}%</b></div>
          <div class="kpi"><span>Monthly Revenue</span><b>${html(inr(model.summary.monthly_revenue))}</b></div>
        </div>
        <div class="note">This PDF is an executive version. Use the Excel download for the complete linked operating dataset across clients, campaigns, hoardings, and campaign-hoarding assignments.</div>
        ${sectionTable("City Performance", cityRows, [
          { key: "city", label: "City" }, { key: "total", label: "Total" }, { key: "occupied", label: "Occupied" },
          { key: "occupancy_pct", label: "Occ %" }, { label: "Revenue", value: (r) => inr(r.revenue) },
        ])}
        ${sectionTable("Top Clients", topClients, [
          { label: "Client", value: (r) => r.company }, { key: "business_type", label: "Industry" },
          { key: "active_hoardings", label: "Active" }, { label: "Monthly Spend", value: (r) => inr(r.monthly_spend) },
          { label: "Last Booking", value: (r) => fmtDate(r.last_booking_end) },
        ])}
        ${sectionTable("Campaign Snapshot", campaignRows, [
          { label: "Campaign", value: (r) => r.name || compactId(getCampaignId(r)) }, { key: "client_name", label: "Client" },
          { key: "status", label: "Status" }, { label: "Start", value: (r) => fmtDate(r.start_date) },
          { label: "End", value: (r) => fmtDate(r.end_date) }, { label: "Revenue", value: (r) => inr(r.revenue) },
        ])}
        ${sectionTable("Expiring In 90 Days", expiringRows, [
          { label: "Hoarding", value: (r) => getHoardingCode(r) }, { key: "name", label: "Name" },
          { key: "city", label: "City" }, { key: "client_name", label: "Client" },
          { label: "End", value: (r) => fmtDate(r.campaign_end) },
        ])}
        ${sectionTable("Available Inventory", availableRows, [
          { label: "Hoarding", value: (r) => getHoardingCode(r) }, { key: "name", label: "Name" },
          { key: "city", label: "City" }, { key: "area", label: "Area" }, { key: "type", label: "Type" },
          { label: "Price", value: (r) => inr(r.monthly_price) },
        ])}
        <div class="footer">Hoardexa branded report. Confidential operating data for the current advertiser account.</div>
        <script>window.print()</script>
      </body></html>`);
    win.document.close();
  }

  return (
    <Layout
      title="Reports & Analytics"
      subtitle="Export a complete operating workbook and branded PDF report for leadership reviews."
      actions={(
          <button onClick={load} className="hx-secondary-action" title="Refresh report data" disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
      )}
    >
      <div className="hx-reports-page grid grid-cols-1 gap-5 lg:grid-cols-4">
        <DataPanel title="Report types" subtitle={loading ? "Loading..." : `${REPORT_TYPES.length} exports available`} className="hx-reports-panel lg:col-span-1">
          <div className="space-y-2">
          {REPORT_TYPES.map((rt) => (
            <button
              key={rt.id} onClick={() => setType(rt.id)} data-testid={`report-${rt.id}`}
              className={`hx-report-type ${type === rt.id ? "is-active" : ""}`}
            >
              <div className="text-sm font-semibold">{rt.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{rt.description}</div>
            </button>
          ))}
          </div>
        </DataPanel>

        <DataPanel
          className="hx-reports-panel lg:col-span-3"
          title={REPORT_TYPES.find((r) => r.id === type)?.label}
          subtitle={
            loading ? "Loading report data..." : `${rows.length} preview rows. Excel includes ${model.clients.length} clients, ${model.hoardings.length} hoardings, ${model.campaigns.length} campaigns and ${model.links.length} assignment links`
          }
          actions={canExport && (
            <div className="flex items-center gap-2">
              <button onClick={exportPowerExcel} disabled={loading || exportingExcel} data-testid="report-excel" className="hx-primary-action">
                <FileSpreadsheet size={14} className={exportingExcel ? "animate-pulse" : ""} /> {exportingExcel ? "Preparing..." : "Excel"}
              </button>
              <button onClick={exportPDF} disabled={loading} data-testid="report-pdf" className="hx-primary-action">
                <FileText size={14} /> PDF
              </button>
            </div>
          )}
        >
          <MetricStrip
            metrics={[
              { label: "Clients", value: model.summary.clients, tone: "blue" },
              { label: "Hoardings", value: model.summary.total_hoardings, tone: "indigo" },
              { label: "Occupancy", value: `${model.summary.occupancy_pct}%`, tone: "emerald" },
              { label: "Monthly revenue", value: inr(model.summary.monthly_revenue), tone: "amber" },
            ]}
          />

          <div className="mt-5 overflow-x-auto">
            <TableSurface>
              <table>
                <thead>
                  <tr>
                    {cols.map((c) => <th key={c}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 30).map((r, i) => (
                    <tr key={i}>
                      {cols.map((c) => <td key={c} className="text-slate-700">{r[c] ?? "-"}</td>)}
                    </tr>
                  ))}
                  {!loading && rows.length === 0 && (<tr><td colSpan={cols.length || 1}><EmptyState title="No data for this report" description="Try a different report type or refresh the source data." /></td></tr>)}
                  {loading && (<tr><td colSpan={cols.length || 1}><EmptyState title="Preparing report data" description="Building the report preview..." /></td></tr>)}
                </tbody>
              </table>
            </TableSurface>
          </div>
        </DataPanel>
      </div>
    </Layout>
  );
}
